import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import supabase from '../../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import Button from '../ui/Button';

const { FiEdit2, FiX, FiSave, FiPlus, FiTrash2 } = FiIcons;

// Utility: deep clone simple objects
const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));

const defaultPricing = { online: {}, home: {} };

export default function ServiceInlineEditor({ serviceIdOverride, openOverride, onCloseOverride }) {
  const { user } = useAuth();
  const location = useLocation();

  const onServiceDetailsPath = location.pathname.startsWith('/service-details/');
  const serviceId = useMemo(() => (serviceIdOverride ? serviceIdOverride : (onServiceDetailsPath ? location.pathname.split('/').pop() : null)), [location, onServiceDetailsPath, serviceIdOverride]);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serviceRow, setServiceRow] = useState(null);
  const [lastDate, setLastDate] = useState('');
  const [pricing, setPricing] = useState(defaultPricing);
  const [serviceName, setServiceName] = useState('');
  const [usingPerService, setUsingPerService] = useState(false);
  const [perTableName, setPerTableName] = useState('');

  useEffect(() => {
    // Reset when route changes
    setOpen(false);
    setServiceRow(null);
    setPricing(defaultPricing);
    setLastDate('');
  }, [serviceId]);

  const isAdminOnDetails = user?.role === 'admin' && !!serviceId;

  const fetchService = async () => {
    if (!serviceId) return;
    try {
      setLoading(true);
      // 1) If this slug maps to a per-service table, load latest row.data from that table
      const perServiceTables = {
        'jee-mains': 'svc_jee_mains_f5e34b8f',
        'amrita': 'svc_amrita_bfc3b0b6',
        'ap-eapcet': 'svc_ap_eapcet_d211a441',
        'neet': 'svc_neet_00756c41',
        'visa-applications': 'svc_visa_applications_4a46ed8c',
        'passport-applications': 'svc_passport_applications_5740b2a9',
        'tg-eapcet': 'svc_tg_eapcet_c48ce354',
        'srm': 'svc_srm_srmjeee_70becc04',
        'bitsat': 'svc_bitsat_a2247cf8',
        'iiit-hyderabad': 'svc_iiit_hyderabad_23ecda96',
        'other-universities': 'svc_other_universities_e6babfe7',
        'job-applications': 'svc_job_applications_1be41f5d',
        'resume-building': 'svc_resume_building_ace6aebe',
        'career-guidance': 'svc_career_guidance_85bbe5fa',
        'government-job-forms': 'svc_government_job_forms_032f1d65',
        'vit': 'svc_vit_381766f8',
        'document-verification': 'svc_document_verification_4b15b913',
        'other-government-forms': 'svc_other_government_forms_72d19ea5',
      };
      const tableName = perServiceTables[serviceId];
      if (tableName) {
        const { data: trows, error: terr } = await supabase
          .from(tableName)
          .select('id, data')
          .order('created_at', { ascending: false })
          .limit(1);
        if (terr) throw terr;
        setUsingPerService(true);
        setPerTableName(tableName);
        if (trows && trows.length > 0) {
          const payload = trows[0]?.data || {};
          setServiceName(payload.name || 'Service');
          const dpFromTable = {
            online: payload.onlinePricing || {},
            home: payload.homePricing || {},
          };
          setPricing(deepClone(dpFromTable));
          setLastDate(payload.lastDate || '');
          setServiceRow({ id: trows[0].id });
          return;
        } else {
          // No rows yet in table, initialize empty and keep going
          setServiceName((serviceId || 'Service').replace(/[-_]+/g, ' ').toUpperCase());
          setPricing(defaultPricing);
          setLastDate('');
          setServiceRow(null);
          return;
        }
      }

      // 2) Default: try to match by slug/code/name heuristics in services_dy3xr
      // Fallback: lookup by name pattern only (table may not have slug/code/key columns)
      const { data: rows, error } = await supabase
        .from('services_dy3xr')
        .select('*')
        .ilike('name', `%${serviceId.replace(/[-_]+/g, ' ')}%`)
        .order('name', { ascending: true })
        .limit(1);

      if (error) throw error;

      let row = (rows && rows[0]) || null;

      if (!row) {
        toast.error('Service not found in database for editing');
        return;
      }

      setServiceRow(row);
      setServiceName(row.name || 'Service');
      const dp = deepClone(row.detailed_pricing) || defaultPricing;
      if (!dp.online) dp.online = {};
      if (!dp.home) dp.home = {};
      setPricing(dp);

      const ld = row.last_date || row.lastDate || '';
      setLastDate(ld);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service for editing');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = async () => {
    await fetchService();
    if (openOverride === undefined) setOpen(true);
  };

  const closeEditor = () => {
    if (onCloseOverride) onCloseOverride();
    if (openOverride === undefined) setOpen(false);
  };

  useEffect(() => {
    if (openOverride && serviceId) {
      fetchService();
    }
  }, [openOverride, serviceId]);

  const addCategory = (type) => {
    const name = prompt('Enter category name (e.g., "OC Male", "All Categories")');
    if (!name || !name.trim()) return;
    const n = name.trim();
    setPricing(prev => {
      const next = deepClone(prev);
      if (!next[type]) next[type] = {};
      if (!next[type][n]) next[type][n] = { examFee: 0, serviceCharge: 0, bankCharge: 50, total: 50 };
      return next;
    });
  };

  const removeCategory = (type, cat) => {
    setPricing(prev => {
      const next = deepClone(prev);
      if (next[type] && next[type][cat]) {
        delete next[type][cat];
      }
      return next;
    });
  };

  const onPriceChange = (type, cat, field, value) => {
    const num = parseFloat(value);
    setPricing(prev => {
      const next = deepClone(prev);
      if (!next[type]) next[type] = {};
      if (!next[type][cat]) next[type][cat] = { examFee: 0, serviceCharge: 0, bankCharge: 0, total: 0 };
      next[type][cat][field] = isNaN(num) ? 0 : num;
      const p = next[type][cat];
      p.total = (p.examFee || 0) + (p.serviceCharge || 0) + (p.bankCharge || 0);
      return next;
    });
  };

  const handleSave = async () => {
    // Require an existing row for both flows to avoid creating new records here.
    if (!serviceRow) {
      toast.error('No existing record to update for this service.');
      return;
    }
    try {
      setSaving(true);
      if (usingPerService && perTableName) {
        // Update the existing row in the per-service table
        const payload = {
          name: serviceName || 'Service',
          lastDate: lastDate || null,
          onlinePricing: pricing.online || {},
          homePricing: pricing.home || {},
        };
        const { error: perErr } = await supabase
          .from(perTableName)
          .update({ data: payload })
          .eq('id', serviceRow.id);
        if (perErr) throw perErr;
      } else {
        const { error } = await supabase
          .from('services_dy3xr')
          .update({
            name: serviceName || 'Service',
            detailed_pricing: pricing,
            last_date: lastDate || null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', serviceRow.id);
        if (error) throw error;
      }
      toast.success('Service updated successfully');
      setOpen(false);
    } catch (e) {
      console.error(e);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (!isAdminOnDetails) return null;

  return (
    <>
      {/* Floating admin edit button */}
      {openOverride === undefined && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button onClick={openEditor} className="flex items-center shadow-lg">
            <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      )}

      {/* Modal */}
      {(openOverride ?? open) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeEditor} />
          <div className="relative bg-white w-full max-w-3xl rounded-lg shadow-xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Edit Service Pricing</h2>
              <button onClick={closeEditor} className="text-gray-500 hover:text-gray-700">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Service Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                <input
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder="e.g., NEET, JEE Mains, Passport Applications"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              {/* Last Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Date</label>
                <input
                  type="text"
                  value={lastDate}
                  onChange={(e) => setLastDate(e.target.value)}
                  placeholder="e.g., 31-12-2024"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              {/* Online Pricing */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Online Service Pricing</h3>
                  <Button variant="outline" size="sm" onClick={() => addCategory('online')}>
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add Category
                  </Button>
                </div>
                <div className="space-y-3">
                  {Object.entries(pricing.online || {}).map(([cat, p]) => (
                    <div key={`online-${cat}`} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{cat}</span>
                        <button className="text-red-600 text-sm" onClick={() => removeCategory('online', cat)}>
                          <SafeIcon icon={FiTrash2} className="w-4 h-4 inline mr-1" />Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <label className="block">
                          <span className="text-gray-600">Exam Fee</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.examFee ?? 0}
                            onChange={(e) => onPriceChange('online', cat, 'examFee', e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600">Service Charge</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.serviceCharge ?? 0}
                            onChange={(e) => onPriceChange('online', cat, 'serviceCharge', e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600">Bank Charges and Taxes</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.bankCharge ?? 0}
                            onChange={(e) => onPriceChange('online', cat, 'bankCharge', e.target.value)} />
                        </label>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">Total: {(p.total ?? ((p.examFee||0)+(p.serviceCharge||0)+(p.bankCharge||0))).toString()}</div>
                    </div>
                  ))}
                  {Object.keys(pricing.online || {}).length === 0 && (
                    <div className="text-sm text-gray-500">No online categories. Use Add Category.</div>
                  )}
                </div>
              </div>

              {/* Home Pricing */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold">Home Service Pricing</h3>
                  <Button variant="outline" size="sm" onClick={() => addCategory('home')}>
                    <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
                    Add Category
                  </Button>
                </div>
                <div className="space-y-3">
                  {Object.entries(pricing.home || {}).map(([cat, p]) => (
                    <div key={`home-${cat}`} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{cat}</span>
                        <button className="text-red-600 text-sm" onClick={() => removeCategory('home', cat)}>
                          <SafeIcon icon={FiTrash2} className="w-4 h-4 inline mr-1" />Remove
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-3 text-sm">
                        <label className="block">
                          <span className="text-gray-600">Exam Fee</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.examFee ?? 0}
                            onChange={(e) => onPriceChange('home', cat, 'examFee', e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600">Service Charge</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.serviceCharge ?? 0}
                            onChange={(e) => onPriceChange('home', cat, 'serviceCharge', e.target.value)} />
                        </label>
                        <label className="block">
                          <span className="text-gray-600">Bank Charges and Taxes</span>
                          <input type="number" className="w-full border rounded px-2 py-1"
                            value={p.bankCharge ?? 0}
                            onChange={(e) => onPriceChange('home', cat, 'bankCharge', e.target.value)} />
                        </label>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">Total: {(p.total ?? ((p.examFee||0)+(p.serviceCharge||0)+(p.bankCharge||0))).toString()}</div>
                    </div>
                  ))}
                  {Object.keys(pricing.home || {}).length === 0 && (
                    <div className="text-sm text-gray-500">No home categories. Use Add Category.</div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={closeEditor} className="flex items-center">
                  <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving || loading} className="flex items-center">
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
